import { Fragment, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { clsx }            from 'clsx'
import { useCompetitionTeams, usePaginatedPlayerList, PLAYERS_PAGE_SIZE_PAGINATED } from '../queries'
import type { PlayerSearch } from '../hooks/usePlayerSearch'
import { useMediaQuery }   from '@/shared/hooks/useMediaQuery'
import type { PlayerListItem, SquadDraft } from '../types'
import { PlayerFilterBar }  from './PlayerFilterBar'
import { PlayerCard }       from './PlayerCard'
import { PlayerStatsSheet } from './PlayerStatsSheet'
import styles from './PlayerListPanel.module.css'

// ── Props ──────────────────────────────────────────────────────────────────────

type PlayerListPanelProps = {
  draft:           SquadDraft
  draftIds:        ReadonlySet<string>
  isWindowOpen:    boolean
  competitionId:   string
  budgetRemaining: number   // drives the "Em orçamento" price filter
  search:          PlayerSearch   // lifted to SquadPicker so the pitch can drive the position filter
  onAdd:           (player: PlayerListItem) => void
  onRemove:        (playerId: string) => void
}

// ── Column header definitions ──────────────────────────────────────────────────

type SortCol = 'name' | 'points' | 'lastGw' | 'avgPoints' | 'price'

const STAT_COLS: Array<{ id: SortCol; label: string; title: string }> = [
  { id: 'points',    label: 'Pts',   title: 'Ordenar por pontos totais'  },
  { id: 'avgPoints', label: 'Méd',   title: 'Ordenar por média de pontos' },
  { id: 'price',     label: 'Preço', title: 'Ordenar por preço'           },
]

// ── Component ──────────────────────────────────────────────────────────────────

export function PlayerListPanel({
  draft,
  draftIds,
  isWindowOpen,
  competitionId,
  budgetRemaining,
  search,
  onAdd,
  onRemove,
}: PlayerListPanelProps) {
  const {
    filter,
    serverFilter,
    page,
    setPage,
    setSearch,
    setPosition,
    setTeamCode,
    setSortBy,
    toggleHideEliminated,
    setPriceRange,
    toggleOnBudget,
    resetFilter,
  } = search

  const { data: teams = [] } = useCompetitionTeams(competitionId)

  const effectiveFilter = useMemo(
    () => ({ ...serverFilter, competitionId }),
    [serverFilter, competitionId],
  )

  // Pagination (page + reset-on-filter-change) lives in usePlayerSearch so it
  // survives the mobile pitch/list tab switch that unmounts this panel.
  const skip = page * PLAYERS_PAGE_SIZE_PAGINATED

  const { data, isPending, isError, refetch } = usePaginatedPlayerList(effectiveFilter, skip)

  const pageItems  = data?.items ?? []
  const total      = data?.total ?? 0
  const hasMore    = data?.hasMore ?? false
  const totalPages = total > 0 ? Math.ceil(total / PLAYERS_PAGE_SIZE_PAGINATED) : 1
  const priceMin   = data?.priceMin ?? 0
  const priceMax   = data?.priceMax ?? 0

  // ── Expanded player (detail sheet) ───────────────────────────────────────
  const [expandedPlayerId, setExpandedPlayerId] = useState<string | null>(null)
  const isMobile = !useMediaQuery('(min-width: 768px)')

  // Reset expanded player when filters or page change
  useEffect(() => {
    setExpandedPlayerId(null)
  }, [serverFilter, filter, page])

  const expandedPlayer = pageItems.find(p => p.playerId === expandedPlayerId) ?? null

  // ── Loading / error states ────────────────────────────────────────────────
  if (isPending) {
    return (
      <div className={styles.panel}>
        <div className={styles.statusWrap}>
          <div className={styles.loadingSpinner} aria-label="Carregando jogadores…" />
          <p className={styles.statusText}>Carregando jogadores…</p>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={styles.panel}>
        <div className={styles.statusWrap}>
          <p className={styles.statusText}>Não foi possível carregar os jogadores.</p>
          <button className={styles.retryBtn} onClick={() => void refetch()}>
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.panel}>
      <PlayerFilterBar
        filter={filter}
        teams={teams}
        priceMin={priceMin}
        priceMax={priceMax}
        budgetRemaining={budgetRemaining}
        onSearch={setSearch}
        onSetPosition={setPosition}
        onSetTeamCode={setTeamCode}
        onToggleHideElim={toggleHideEliminated}
        onSetPriceRange={setPriceRange}
        onToggleOnBudget={toggleOnBudget}
        onReset={resetFilter}
      />

      {/* Sortable column header */}
      <div className={styles.listHeader} aria-label="Ordenar jogadores">
        <div className={styles.headerBadge} aria-hidden="true" />

        <button
          className={clsx(
            styles.headerColPlayer,
            filter.sortBy === 'name' && styles.headerColActive,
          )}
          onClick={() => setSortBy('name')}
          title="Ordenar por nome"
        >
          Jogador
          {filter.sortBy === 'name' && (
            <ChevronDown
              size={10}
              className={clsx(styles.sortArrow, filter.sortDir === 'asc' && styles.sortArrowAsc)}
              aria-hidden="true"
            />
          )}
        </button>

        {STAT_COLS.map(col => (
          <Fragment key={col.id}>
            <button
              className={clsx(
                styles.headerColStat,
                col.id === 'price' && styles.headerColPrice,
                filter.sortBy === col.id && styles.headerColActive,
              )}
              onClick={() => setSortBy(col.id)}
              title={col.title}
            >
              {col.label}
              {filter.sortBy === col.id && (
                <ChevronDown
                  size={10}
                  className={clsx(styles.sortArrow, filter.sortDir === 'asc' && styles.sortArrowAsc)}
                  aria-hidden="true"
                />
              )}
            </button>
            {/* Last-GW points sits beside total points — sortable */}
            {col.id === 'points' && (
              <button
                className={clsx(
                  styles.headerColStat,
                  styles.headerLast,
                  filter.sortBy === 'lastGw' && styles.headerColActive,
                )}
                onClick={() => setSortBy('lastGw')}
                title="Ordenar por pontos da última rodada"
              >
                Últ
                {filter.sortBy === 'lastGw' && (
                  <ChevronDown
                    size={10}
                    className={clsx(styles.sortArrow, filter.sortDir === 'asc' && styles.sortArrowAsc)}
                    aria-hidden="true"
                  />
                )}
              </button>
            )}
          </Fragment>
        ))}

        <div className={styles.headerAction} aria-hidden="true" />
      </div>

      {/* Summary bar */}
      <div className={styles.summary} aria-live="polite">
        <span className={styles.summaryCount}>
          {total} jogadores
        </span>
        {draftIds.size > 0 && (
          <span className={styles.summaryInSquad}>{draftIds.size} na escalação</span>
        )}
      </div>

      {/* Player list — no scroll, fixed 8 rows per page */}
      {pageItems.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>🔍</span>
          <p className={styles.emptyTitle}>Nenhum jogador encontrado</p>
          <p className={styles.emptyBody}>Tente outros termos ou ajuste os filtros.</p>
          <button className={styles.retryBtn} onClick={resetFilter}>Limpar filtros</button>
        </div>
      ) : (
        <div
          className={styles.list}
          role="list"
          aria-label={`${pageItems.length} jogadores`}
        >
          {pageItems.map(player => (
            <PlayerCard
              key={player.playerId}
              player={player}
              draft={draft}
              isWindowOpen={isWindowOpen}
              isInSquad={draftIds.has(player.playerId)}
              onAdd={onAdd}
              onRemove={onRemove}
              isExpanded={expandedPlayerId === player.playerId}
              onExpand={() => setExpandedPlayerId(prev =>
                prev === player.playerId ? null : player.playerId
              )}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}

      {/* Pagination — always rendered for layout stability */}
      <div className={styles.paginationBar}>
        <button
          className={styles.pageBtn}
          onClick={() => setPage(p => p - 1)}
          disabled={page === 0}
          aria-label="Página anterior"
        >
          <ChevronLeft size={14} />
        </button>
        <span className={styles.pageInfo}>
          {page + 1} / {totalPages}
        </span>
        <button
          className={styles.pageBtn}
          onClick={() => setPage(p => p + 1)}
          disabled={!hasMore}
          aria-label="Próxima página"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Mobile bottom sheet */}
      {isMobile && expandedPlayer !== null && (
        <PlayerStatsSheet
          player={expandedPlayer}
          isInSquad={draftIds.has(expandedPlayer.playerId)}
          isWindowOpen={isWindowOpen}
          onAdd={onAdd}
          onRemove={onRemove}
          onClose={() => setExpandedPlayerId(null)}
        />
      )}
    </div>
  )
}
